import katex from 'katex';

/**
 * Render LaTeX math expression to HTML
 */
export const renderMath = (expression: string, displayMode: boolean = false): string => {
  try {
    return katex.renderToString(expression, {
      displayMode,
      throwOnError: false,
      errorColor: '#cc0000',
      strict: false,
      trust: true,
      macros: {
        // Common mathematical macros
        '\\RR': '\\mathbb{R}',
        '\\NN': '\\mathbb{N}',
        '\\ZZ': '\\mathbb{Z}',
        '\\QQ': '\\mathbb{Q}',
        '\\CC': '\\mathbb{C}',
        '\\dd': '\\mathrm{d}',
        '\\ee': '\\mathrm{e}',
        '\\ii': '\\mathrm{i}',
        
        // Physics and chemistry macros
        '\\degree': '^\\circ',
        '\\celsius': '^\\circ\\mathrm{C}',
        '\\fahrenheit': '^\\circ\\mathrm{F}',
        '\\kelvin': '\\mathrm{K}',
        '\\mol': '\\mathrm{mol}',
        '\\kg': '\\mathrm{kg}',
        '\\ms': '\\mathrm{m/s}',
        '\\mss': '\\mathrm{m/s^2}',
        '\\newton': '\\mathrm{N}',
        '\\joule': '\\mathrm{J}',
        '\\watt': '\\mathrm{W}',
        '\\volt': '\\mathrm{V}',
        '\\ampere': '\\mathrm{A}',
        '\\ohm': '\\Omega',
        
        // Vector notation
        '\\vec#1': '\\overrightarrow{#1}',
        '\\uvec#1': '\\hat{#1}',
        
        // Common functions
        '\\grad': '\\nabla',
        '\\divergence': '\\nabla \\cdot',
        '\\curl': '\\nabla \\times',
        '\\laplacian': '\\nabla^2',
        
        // Aligned equation shortcuts
        '\\beq': '\\begin{align}',
        '\\eeq': '\\end{align}',
        '\\beqs': '\\begin{align*}',
        '\\eeqs': '\\end{align*}',
        
        // Matrix shortcuts
        '\\bmat': '\\begin{pmatrix}',
        '\\emat': '\\end{pmatrix}',
        '\\bvec': '\\begin{pmatrix}',
        '\\evec': '\\end{pmatrix}',
        '\\bdet': '\\begin{vmatrix}',
        '\\edet': '\\end{vmatrix}',
        
        // Hebrew mathematical terms (optional)
        '\\hebraicalpha': '\\aleph',
        '\\hebrewbeth': '\\beth',
        '\\hebrewgimel': '\\gimel',
        '\\hebrewdaleth': '\\daleth',
      }
    });
  } catch (error) {
    console.error('KaTeX rendering error:', error);
    return `<span class="math-error" style="color: #cc0000; border: 1px solid #cc0000; padding: 2px 4px; border-radius: 3px;">[Math Error: ${expression}]</span>`;
  }
};

/**
 * Check if a string contains LaTeX math expressions
 */
export const containsMath = (text: string): boolean => {
  // Check for inline math delimiters: $...$ or \(...\)
  const inlineMathRegex = /\$[^$\n]+\$|\\\([^)]+\\\)/g;
  // Check for display math delimiters: $$...$$ or \[...\]
  const displayMathRegex = /\$\$[\s\S]+?\$\$|\\\[[\s\S]+?\\\]/g;
  
  return inlineMathRegex.test(text) || displayMathRegex.test(text);
};

/**
 * Extract math expressions from text
 */
export const extractMathExpressions = (text: string): Array<{ expression: string; type: 'inline' | 'display'; start: number; end: number }> => {
  const expressions: Array<{ expression: string; type: 'inline' | 'display'; start: number; end: number }> = [];
  
  // Find display math first (higher priority)
  const displayRegex = /\$\$([\s\S]*?)\$\$|\\\[([\s\S]*?)\\\]/g;
  let match: RegExpExecArray | null;
  
  while ((match = displayRegex.exec(text)) !== null) {
    expressions.push({
      expression: match[1] || match[2],
      type: 'display',
      start: match.index,
      end: match.index + match[0].length
    });
  }
  
  // Find inline math, avoiding display math areas
  const inlineRegex = /\$([^$\n]+)\$|\\\(([^)]+)\\\)/g;
  
  while ((match = inlineRegex.exec(text)) !== null) {
    // Check if this match overlaps with any display math
    const isInDisplayMath = expressions.some(expr => 
      match!.index >= expr.start && match!.index < expr.end
    );
    
    if (!isInDisplayMath) {
      expressions.push({
        expression: match[1] || match[2],
        type: 'inline',
        start: match!.index,
        end: match!.index + match[0].length
      });
    }
  }
  
  return expressions.sort((a, b) => a.start - b.start);
};

/**
 * Validate LaTeX syntax
 */
export const validateLatex = (expression: string): { valid: boolean; error?: string } => {
  try {
    katex.renderToString(expression, { throwOnError: true });
    return { valid: true };
  } catch (error) {
    return { 
      valid: false, 
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Preprocess content to fix incorrect math delimiters from the AI model
 * The model sometimes incorrectly uses `( ... )` for inline math and `[ ... ]` for display math.
 * This function converts them to the correct `$ ... $` and `$$ ... $$` delimiters.
 */
export const preprocessBadMathFormats = (content: string): string => {
  if (!content) {
    return content;
  }

  const protectedBlocks: string[] = [];
  let processed = content.replace(/```[\s\S]*?```/g, block => {
    const placeholder = `__CODEBLOCK_PLACEHOLDER_${protectedBlocks.length}__`;
    protectedBlocks.push(block);
    return placeholder;
  });

  processed = processed.replace(/^\[(.+?)\]$/gm, (match, inner) => {
    const trimmed = inner.trim();
    if (!trimmed) {
      return match;
    }

    const looksLikeTableRow = /^"[^"]*"(,\s*"[^"]*")+$/.test(trimmed);
    if (looksLikeTableRow) {
      return match;
    }

    const hasMathIndicators = /\\|=|[^ ]\^|_/.test(trimmed);
    if (!hasMathIndicators) {
      return match;
    }

    return `$$${inner}$$`;
  });

  processed = processed.replace(/\( (.+?) \)/g, (match, inner) => {
    const trimmed = inner.trim();
    if (!trimmed) {
      return match;
    }

    const hasMathIndicators = /\\|=|[^ ]\^|_/.test(trimmed);
    if (!hasMathIndicators) {
      return match;
    }

    return `$${inner}$`;
  });

  return processed.replace(/__CODEBLOCK_PLACEHOLDER_(\d+)__/g, (_, index) => {
    const blockIndex = Number(index);
    return protectedBlocks[blockIndex] ?? '';
  });
};


/**
 * Convert common superscript HTML tags to LaTeX
 * This provides backward compatibility for existing content
 */
export const convertSupTagsToLatex = (text: string): string => {
  // Convert <sup>...</sup> to ^{...}
  return text.replace(/<sup[^>]*>(.*?)<\/sup>/gi, '^{$1}');
};

/**
 * Convert common subscript HTML tags to LaTeX
 */
export const convertSubTagsToLatex = (text: string): string => {
  // Convert <sub>...</sub> to _{...}
  return text.replace(/<sub[^>]*>(.*?)<\/sub>/gi, '_{$1}');
};

/**
 * Preprocess text to convert HTML math tags to LaTeX
 */
const LOOSE_ARROW_REGEX = /\\{1,2}(Rightarrow|rightarrow|Leftarrow|leftarrow|Leftrightarrow|leftrightarrow)/g;

const LOOSE_ARROW_MAP: Record<string, string> = {
  Rightarrow: '⇒',
  rightarrow: '→',
  Leftarrow: '⇐',
  leftarrow: '←',
  Leftrightarrow: '⇔',
  leftrightarrow: '↔',
};

const replaceArrowsInSegment = (segment: string): string =>
  segment.replace(LOOSE_ARROW_REGEX, (_, command: keyof typeof LOOSE_ARROW_MAP) => LOOSE_ARROW_MAP[command] ?? _);

export const replaceLooseLatexArrows = (text: string): string => {
  if (!text) {
    return text;
  }

  const mathExpressions = extractMathExpressions(text);
  if (!mathExpressions.length) {
    return replaceArrowsInSegment(text);
  }

  let result = '';
  let lastIndex = 0;

  for (const expr of mathExpressions) {
    if (lastIndex < expr.start) {
      result += replaceArrowsInSegment(text.slice(lastIndex, expr.start));
    }
    result += text.slice(expr.start, expr.end);
    lastIndex = expr.end;
  }

  if (lastIndex < text.length) {
    result += replaceArrowsInSegment(text.slice(lastIndex));
  }

  return result;
};

export const preprocessMathTags = (text: string): string => {
  let processed = text;
  processed = convertSupTagsToLatex(processed);
  processed = convertSubTagsToLatex(processed);
  processed = replaceLooseLatexArrows(processed);
  return processed;
};

/**
 * Common math patterns and their LaTeX equivalents
 */
export const mathPatterns = {
  // Greek letters
  alpha: '\\alpha',
  beta: '\\beta', 
  gamma: '\\gamma',
  delta: '\\delta',
  epsilon: '\\epsilon',
  pi: '\\pi',
  sigma: '\\sigma',
  theta: '\\theta',
  
  // Common symbols
  infinity: '\\infty',
  integral: '\\int',
  sum: '\\sum',
  product: '\\prod',
  sqrt: '\\sqrt',
  
  // Relations
  leq: '\\leq',
  geq: '\\geq',
  neq: '\\neq',
  approx: '\\approx',
  equiv: '\\equiv',
};

/**
 * Auto-suggest LaTeX commands based on text input
 */
export const suggestLatexCommands = (input: string): string[] => {
  const suggestions: string[] = [];
  const lowerInput = input.toLowerCase();
  
  Object.entries(mathPatterns).forEach(([key, value]) => {
    if (key.includes(lowerInput) || value.includes(lowerInput)) {
      suggestions.push(value);
    }
  });
  
  return suggestions;
};

/**
 * Helper functions for creating common mathematical structures
 */

/**
 * Create a matrix LaTeX string from a 2D array
 */
export const createMatrix = (data: (string | number)[][], matrixType: 'pmatrix' | 'bmatrix' | 'vmatrix' | 'Vmatrix' = 'pmatrix'): string => {
  if (!data.length || !data[0].length) return '';
  
  const rows = data.map(row => row.join(' & ')).join(' \\\\ ');
  return `\\begin{${matrixType}}\n${rows}\n\\end{${matrixType}}`;
};

/**
 * Create a vector LaTeX string from an array
 */
export const createVector = (data: (string | number)[], matrixType: 'pmatrix' | 'bmatrix' = 'pmatrix'): string => {
  if (!data.length) return '';
  
  const elements = data.join(' \\\\ ');
  return `\\begin{${matrixType}}\n${elements}\n\\end{${matrixType}}`;
};

/**
 * Create aligned equations LaTeX string
 */
export const createAlignedEquations = (equations: string[], numbered: boolean = true): string => {
  const alignType = numbered ? 'align' : 'align*';
  const eqString = equations.join(' \\\\ ');
  return `\\begin{${alignType}}\n${eqString}\n\\end{${alignType}}`;
};

/**
 * Create a fraction with proper formatting
 */
export const createFraction = (numerator: string, denominator: string): string => {
  return `\\frac{${numerator}}{${denominator}}`;
};

/**
 * Create a square root with optional index
 */
export const createRoot = (expression: string, index?: string): string => {
  if (index) {
    return `\\sqrt[${index}]{${expression}}`;
  }
  return `\\sqrt{${expression}}`;
};

/**
 * Create integral notation
 */
export const createIntegral = (
  expression: string, 
  variable: string = 'x', 
  lowerLimit?: string, 
  upperLimit?: string,
  type: 'int' | 'iint' | 'iiint' | 'oint' = 'int'
): string => {
  let integral = `\\${type}`;
  
  if (lowerLimit !== undefined || upperLimit !== undefined) {
    integral += `_{${lowerLimit || ''}}^{${upperLimit || ''}}`;
  }
  
  return `${integral} ${expression} \\, d${variable}`;
};

/**
 * Create summation notation
 */
export const createSummation = (
  expression: string,
  variable: string = 'i',
  lowerLimit?: string,
  upperLimit?: string
): string => {
  let sum = '\\sum';
  
  if (lowerLimit !== undefined || upperLimit !== undefined) {
    sum += `_{${variable}=${lowerLimit || ''}}^{${upperLimit || ''}}`;
  }
  
  return `${sum} ${expression}`;
};

/**
 * Create product notation
 */
export const createProduct = (
  expression: string,
  variable: string = 'i',
  lowerLimit?: string,
  upperLimit?: string
): string => {
  let prod = '\\prod';
  
  if (lowerLimit !== undefined || upperLimit !== undefined) {
    prod += `_{${variable}=${lowerLimit || ''}}^{${upperLimit || ''}}`;
  }
  
  return `${prod} ${expression}`;
};

/**
 * Chemical equation helpers
 */
export const createChemicalFormula = (formula: string): string => {
  // Convert numbers to subscripts in chemical formulas
  return formula.replace(/(\d+)/g, '_{$1}');
};

/**
 * Create chemical reaction equation
 */
export const createChemicalReaction = (reactants: string[], products: string[], arrow: string = '\\rightarrow'): string => {
  const reactantStr = reactants.map(createChemicalFormula).join(' + ');
  const productStr = products.map(createChemicalFormula).join(' + ');
  return `${reactantStr} ${arrow} ${productStr}`;
};

/**
 * Physics unit formatting
 */
export const formatPhysicsUnit = (value: string | number, unit: string): string => {
  return `${value}\\,\\mathrm{${unit}}`;
};

/**
 * Create a physics equation with units
 */
export const createPhysicsEquation = (
  leftSide: string,
  rightSide: string,
  leftUnit?: string,
  rightUnit?: string
): string => {
  let equation = `${leftSide} = ${rightSide}`;
  
  if (leftUnit || rightUnit) {
    equation += ` \\quad [${leftUnit || rightUnit}]`;
  }
  
  return equation;
};

/**
 * RTL-safe math processing utilities
 * Prevents Hebrew text direction from affecting math equation rendering
 */

/**
 * Unicode bidirectional control characters
 */
export const BidiChars = {
  LTR_MARK: '\u200E', // Left-to-Right Mark
  RTL_MARK: '\u200F', // Right-to-Left Mark
  LTR_EMBED: '\u202A', // Left-to-Right Embedding
  RTL_EMBED: '\u202B', // Right-to-Left Embedding
  POP_DIRECTIONAL: '\u202C', // Pop Directional Formatting
  LTR_ISOLATE: '\u2066', // Left-to-Right Isolate
  RTL_ISOLATE: '\u2067', // Right-to-Left Isolate
  FIRST_STRONG_ISOLATE: '\u2068', // First Strong Isolate
  POP_DIRECTIONAL_ISOLATE: '\u2069', // Pop Directional Isolate
};

/**
 * Wrap math expression with LTR isolation to prevent RTL interference
 */
export const isolateMathForRTL = (mathHtml: string): string => {
  return `${BidiChars.LTR_ISOLATE}${mathHtml}${BidiChars.POP_DIRECTIONAL_ISOLATE}`;
};

/**
 * Render LaTeX math expression with RTL safety
 */
export const renderMathRTLSafe = (expression: string, displayMode: boolean = false): string => {
  try {
    const rendered = katex.renderToString(expression, {
      displayMode,
      throwOnError: false,
      errorColor: '#cc0000',
      strict: false,
      trust: true,
      macros: {
        // Common mathematical macros
        '\\RR': '\\mathbb{R}',
        '\\NN': '\\mathbb{N}',
        '\\ZZ': '\\mathbb{Z}',
        '\\QQ': '\\mathbb{Q}',
        '\\CC': '\\mathbb{C}',
        '\\dd': '\\mathrm{d}',
        '\\ee': '\\mathrm{e}',
        '\\ii': '\\mathrm{i}',
        
        // Physics and chemistry macros
        '\\degree': '^\\circ',
        '\\celsius': '^\\circ\\mathrm{C}',
        '\\fahrenheit': '^\\circ\\mathrm{F}',
        '\\kelvin': '\\mathrm{K}',
        '\\mol': '\\mathrm{mol}',
        '\\kg': '\\mathrm{kg}',
        '\\ms': '\\mathrm{m/s}',
        '\\mss': '\\mathrm{m/s^2}',
        '\\newton': '\\mathrm{N}',
        '\\joule': '\\mathrm{J}',
        '\\watt': '\\mathrm{W}',
        '\\volt': '\\mathrm{V}',
        '\\ampere': '\\mathrm{A}',
        '\\ohm': '\\Omega',
        
        // Vector notation
        '\\vec#1': '\\overrightarrow{#1}',
        '\\uvec#1': '\\hat{#1}',
        
        // Common functions
        '\\grad': '\\nabla',
        '\\divergence': '\\nabla \\cdot',
        '\\curl': '\\nabla \\times',
        '\\laplacian': '\\nabla^2',
        
        // Aligned equation shortcuts
        '\\beq': '\\begin{align}',
        '\\eeq': '\\end{align}',
        '\\beqs': '\\begin{align*}',
        '\\eeqs': '\\end{align*}',
        
        // Matrix shortcuts
        '\\bmat': '\\begin{pmatrix}',
        '\\emat': '\\end{pmatrix}',
        '\\bvec': '\\begin{pmatrix}',
        '\\evec': '\\end{pmatrix}',
        '\\bdet': '\\begin{vmatrix}',
        '\\edet': '\\end{vmatrix}',
        
        // Hebrew mathematical terms (optional)
        '\\hebraicalpha': '\\aleph',
        '\\hebrewbeth': '\\beth',
        '\\hebrewgimel': '\\gimel',
        '\\hebrewdaleth': '\\daleth',
      }
    });
    
    // Wrap with LTR isolation for RTL safety
    return isolateMathForRTL(rendered);
  } catch (error) {
    console.error('KaTeX rendering error:', error);
    const errorHtml = `<span class="math-error" style="color: #cc0000; border: 1px solid #cc0000; padding: 2px 4px; border-radius: 3px;">[Math Error: ${expression}]</span>`;
    return isolateMathForRTL(errorHtml);
  }
};

/**
 * Process math expressions in RTL text context
 * Ensures math expressions maintain LTR direction even in Hebrew text
 */
export const processMathInRTLContext = (text: string): string => {
  // First, process any HTML tags
  let processed = preprocessMathTags(text);
  
  // Find and isolate inline math expressions
  processed = processed.replace(/\$([^$\n]+)\$/g, (match, expression) => {
    const rendered = renderMathRTLSafe(expression, false);
    return `<span dir="ltr" style="direction: ltr; unicode-bidi: isolate; display: inline-block;">${rendered}</span>`;
  });
  
  // Find and isolate display math expressions
  processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (match, expression) => {
    const rendered = renderMathRTLSafe(expression, true);
    return `<div dir="ltr" style="direction: ltr; unicode-bidi: isolate; text-align: center;">${rendered}</div>`;
  });
  
  // Handle LaTeX delimiters
  processed = processed.replace(/\\\(([^)]+)\\\)/g, (match, expression) => {
    const rendered = renderMathRTLSafe(expression, false);
    return `<span dir="ltr" style="direction: ltr; unicode-bidi: isolate; display: inline-block;">${rendered}</span>`;
  });
  
  processed = processed.replace(/\\\[([\s\S]*?)\\\]/g, (match, expression) => {
    const rendered = renderMathRTLSafe(expression, true);
    return `<div dir="ltr" style="direction: ltr; unicode-bidi: isolate; text-align: center;">${rendered}</div>`;
  });
  
  return processed;
}; 
